/**
 * ScannerOptions — ปุ่มเปิด popup ของคำสั่งที่เป็นเรื่อง "ตัวคน" ไม่ใช่เรื่องของงาน
 * (เปลี่ยนรหัสผ่าน, ออกจากระบบ) บนแถบบนของเครื่องสแกน (D-109 ข้อ 5, ยกเงื่อนไข
 * จาก D-107 ข้อ 7 มาใช้)
 *
 * **ห้าม import `desktop/components/OptionsMenu`** — เครื่องสแกนไม่ใช่การย่อ
 * Desktop UI (CLAUDE.md: ปุ่มขนาด Desktop กดไม่โดนด้วยมือที่สวมถุงมือ) ไฟล์นี้
 * ลอกเฉพาะ**พฤติกรรม**ของ popup ฝั่ง Desktop มา (ปิดด้วย Escape, ปิดเมื่อคลิก
 * นอกกล่อง, คืนโฟกัสกลับปุ่มเปิดหลังปิด, ไม่ดักโฟกัสไว้ข้างใน) แล้วเขียนปุ่ม/
 * รายการของตัวเองด้วย touch target ของหน้างาน (`touch.ts`) แทนขนาดของ Desktop
 *
 * ปุ่มเปิด (ไม่ใช่แค่เนื้อหาข้างใน) ต้อง render และกดได้เสมอ **ไม่มีเงื่อนไขสิทธิ์
 * โมดูลใดทั้งสิ้น**: D-046 แลก session ยาว 16 ชั่วโมงไม่มี idle timeout กับข้อบังคับ
 * ว่าเครื่องสแกนต้องมีทางเปลี่ยนรหัสผ่าน/ออกจากระบบที่กดง่ายเสมอ — คนที่ยังไม่ได้
 * รับสิทธิ์โมดูลใดเลยต้องยังเปลี่ยนรหัสผ่านและออกจากระบบได้ (D-107 ข้อ 7)
 */

import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { SCANNER_PASSWORD_PATH } from './routes';
import { touchTargetStyle } from './touch';
import { SignOutButton } from '../lib/auth/SessionControls';

export function ScannerOptions() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  /** true เฉพาะช่วงที่เพิ่งปิดจาก "เปิดอยู่" — กันไม่ให้แย่งโฟกัสตอนเมานต์ครั้งแรก */
  const wasOpenRef = useRef(false);
  const popupId = useId();

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // กันไม่ให้ Esc ทะลุไปสั่งอย่างอื่นบนหน้าจอที่อยู่หลัง popup
        event.stopPropagation();
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="cap-scanner__options" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="cap-scanner__top-action"
        style={touchTargetStyle}
        data-testid="scanner-options-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={popupId}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        ตัวเลือก
      </button>
      {open ? (
        <div
          id={popupId}
          className="cap-scanner__options-popup"
          aria-label="ตัวเลือก"
          data-testid="scanner-options-popup"
          // กดรายการข้างใน (ลิงก์/ปุ่ม) แล้วปิด popup ทันที — delegate ที่กล่อง
          // แทนที่จะสั่งให้ children แต่ละตัวรู้จัก popup ที่ห่อตัวเองอยู่
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('a, button') !== null) {
              setOpen(false);
            }
          }}
        >
          <Link
            className="cap-scanner__options-item"
            style={touchTargetStyle}
            data-testid="scanner-change-password"
            to={SCANNER_PASSWORD_PATH}
          >
            เปลี่ยนรหัสผ่าน
          </Link>
          <SignOutButton className="cap-scanner__options-item" style={touchTargetStyle} />
        </div>
      ) : null}
    </div>
  );
}
